import { useState, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Gauge, ArrowRight, ArrowLeft, ShieldCheck, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ThemeToggle } from "@/components/site/ThemeToggle";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => params.get("token") || "", [params]);
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres.");
      return;
    }
    if (form.password !== form.confirm) {
      toast.error("Las contraseñas no coinciden.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: form.password });
      setDone(true);
      toast.success("Contraseña restablecida. Inicie sesión.");
      setTimeout(() => navigate("/login", { replace: true }), 1500);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1A1A1F] flex items-center justify-center p-6" data-testid="reset-page">
      <div className="fixed top-4 right-4 z-50">
        <ThemeToggle testId="reset-theme-toggle" />
      </div>
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-2 text-[#A1A1AA] hover:text-[#F59E0B] text-xs font-mono uppercase tracking-widest mb-8" data-testid="reset-back-link">
          <ArrowLeft className="w-3 h-3" /> Volver a iniciar sesión
        </Link>
        <Link to="/" className="inline-flex items-center gap-3 mb-10">
          <div className="w-10 h-10 border border-[#F59E0B] flex items-center justify-center bg-[#F59E0B]/10">
            <Gauge className="w-5 h-5 text-[#F59E0B]" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-heading font-black uppercase text-base">SGM México</div>
            <div className="font-mono text-[10px] text-[#A1A1AA] tracking-widest uppercase">Nueva contraseña</div>
          </div>
        </Link>

        {!token ? (
          <div className="border border-[#EF4444]/40 bg-[#EF4444]/5 p-6" data-testid="reset-no-token">
            <AlertTriangle className="w-10 h-10 text-[#EF4444] mb-3" />
            <h2 className="font-heading font-black uppercase text-2xl mb-2">Enlace inválido</h2>
            <p className="text-[#D4D4D8] text-sm leading-relaxed">El enlace está incompleto. Solicite uno nuevo.</p>
            <Link to="/forgot-password" className="btn-primary mt-6 w-full" data-testid="reset-request-new">
              Solicitar nuevo enlace <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : done ? (
          <div className="border border-emerald-400/40 bg-emerald-400/5 p-6" data-testid="reset-success">
            <CheckCircle2 className="w-10 h-10 text-emerald-400 mb-3" />
            <h2 className="font-heading font-black uppercase text-2xl mb-2">Contraseña actualizada</h2>
            <p className="text-[#D4D4D8] text-sm leading-relaxed">Redirigiendo al inicio de sesión...</p>
          </div>
        ) : (
          <>
            <div className="overline mb-3">// Nueva contraseña</div>
            <h2 className="font-heading font-black uppercase text-3xl mb-2">Restablecer contraseña</h2>
            <p className="text-[#A1A1AA] mb-10">Elija una contraseña segura. Mínimo 6 caracteres.</p>
            <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
              <div>
                <label className="overline block mb-2">Nueva contraseña</label>
                <input
                  required type="password" minLength={6}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="input-base"
                  data-testid="reset-password"
                  autoComplete="new-password"
                />
              </div>
              <div>
                <label className="overline block mb-2">Confirmar contraseña</label>
                <input
                  required type="password" minLength={6}
                  value={form.confirm}
                  onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                  className="input-base"
                  data-testid="reset-confirm"
                  autoComplete="new-password"
                />
              </div>
              <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-50" data-testid="reset-submit">
                {loading ? "Guardando..." : "Guardar nueva contraseña"} <ShieldCheck className="w-4 h-4" />
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
